/**
 * What the cancel journey may offer: the provider pause capability. Server
 * owned; the client never decides eligibility.
 */
import type {
  CancellationReason,
  PlanSet,
  RetentionOptionsResult,
} from '@tabbio/types';
import { api } from '../api';

export const NO_RETENTION_OPTIONS: RetentionOptionsResult = {
  capabilities: { pause: false },
  billingInterval: null,
  tier: 'free',
};

export function parseRetentionOptions(value: unknown): RetentionOptionsResult {
  if (!value || typeof value !== 'object') return NO_RETENTION_OPTIONS;
  const data = value as Record<string, unknown>;
  const capabilities =
    data.capabilities && typeof data.capabilities === 'object'
      ? (data.capabilities as Record<string, unknown>)
      : {};
  const interval = data.billingInterval;
  const tier = data.tier;
  return {
    // Only an explicit `true` enables pause; anything else is the fallback variant.
    capabilities: { pause: capabilities.pause === true },
    billingInterval:
      interval === 'monthly' || interval === 'yearly' ? interval : null,
    tier: tier === 'plus' || tier === 'pro' ? tier : 'free',
  };
}

export function retentionOptionsPath(input: {
  planSet: PlanSet;
  companyId?: string | null;
  reason?: CancellationReason | null;
}) {
  const params = new URLSearchParams({ planSet: input.planSet });
  if (input.companyId) params.set('companyId', input.companyId);
  if (input.reason) params.set('reason', input.reason);
  return `/api/subscription/retention-options?${params.toString()}`;
}

/**
 * GET /api/subscription/retention-options. Throws on an error envelope so the
 * caller can fall back to `NO_RETENTION_OPTIONS` (pause unavailable), which is
 * always a safe journey.
 */
export async function fetchRetentionOptions(input: {
  planSet: PlanSet;
  companyId?: string | null;
  reason?: CancellationReason | null;
}): Promise<RetentionOptionsResult> {
  const response = await api.get<RetentionOptionsResult>(
    retentionOptionsPath(input),
  );
  if (response.error || !response.data) {
    throw new Error(
      response.error?.message ?? 'Retention options returned no data',
    );
  }
  return parseRetentionOptions(response.data);
}
